import { WalletType } from "./wallet-types";
import z from "zod";

/**
 * Margin collateral model of a futures account:
 * - "multi": collateral is pooled across assets (Binance Multi-Assets Mode; Bybit/Crypto.com/Kraken).
 * - "single": collateral is siloed per settlement token (Binance Single-Asset Mode; Bitget/Katana/LBank).
 * Consumers use it to decide whether to aggregate cross-margin collateral or read the settlement token only.
 */
export const MarginAssetModeSchema = z.enum(["single", "multi"]);
export type MarginAssetMode = z.infer<typeof MarginAssetModeSchema>;

export const TokenBalanceTypesSchema = z.object({
  tokenName: z.string().nullable().optional(),
  freeBalance: z.number(),
  refFreeBalance: z.number(),
  usedBalance: z.number(),
  refUsedBalance: z.number(),
  totalBalance: z.number(),
  refTotalBalance: z.number(),
  isAllowedAsCollateral: z.boolean().optional(),
  crossMarginBalanceUsd: z
    .object({
      free: z.number(),
      used: z.number(),
      total: z.number(),
    })
    .optional(),
  marginBalance: z
    .record(
      z.string(),
      z.object({
        freeBalance: z.number(),
        totalBalance: z.number(),
        type: z.enum(["base", "quote"]),
      }),
    )
    .nullable()
    .optional(),
});

export type TokenBalanceTypes = z.infer<typeof TokenBalanceTypesSchema>;

/**
 * Updated version of TokenBalanceTypes
 */
export const PortfolioBalanceEntrySchema = z.object({
  tokenName: z.string().nullable().optional(),
  freeBalance: z.string(),
  refFreeBalance: z.string(),
  usedBalance: z.string(),
  refUsedBalance: z.string(),
  totalBalance: z.string(),
  refTotalBalance: z.string(),
  isAllowedAsCollateral: z.boolean().optional(),
  marginBalance: z
    .record(
      z.string(),
      z.object({
        freeBalance: z.string(),
        totalBalance: z.string(),
        type: z.enum(["base", "quote"]),
      }),
    )
    .nullable()
    .optional(),
});
export type PortfolioBalanceEntry = z.infer<typeof PortfolioBalanceEntrySchema>;

const WalletTypeBalancesSchema = z
  .object({
    [WalletType.SPOT]: TokenBalanceTypesSchema,
    [WalletType.FUNDING]: TokenBalanceTypesSchema,
    [WalletType.ISOLATED_MARGIN]: TokenBalanceTypesSchema,
    [WalletType.CROSS_MARGIN]: TokenBalanceTypesSchema,
    [WalletType.USD_M_FUTURES]: TokenBalanceTypesSchema,
    [WalletType.USDT_M_FUTURES]: TokenBalanceTypesSchema,
    [WalletType.USDC_M_FUTURES]: TokenBalanceTypesSchema,
    [WalletType.COIN_M_FUTURES]: TokenBalanceTypesSchema,
    [WalletType.COIN_M_SWAP]: TokenBalanceTypesSchema,
    [WalletType.OPTIONS]: TokenBalanceTypesSchema,
    [WalletType.EARN]: TokenBalanceTypesSchema,
    [WalletType.DERIVATIVES]: TokenBalanceTypesSchema,
    [WalletType.FUTURES]: TokenBalanceTypesSchema,
    [WalletType.INVERSE_DERIVATIVES]: TokenBalanceTypesSchema,
    [WalletType.EARN_SPOT]: TokenBalanceTypesSchema,
    [WalletType.EARN_INNOVATION]: TokenBalanceTypesSchema,
    [WalletType.INSURANCE_FUND]: TokenBalanceTypesSchema,
    [WalletType.FUND]: TokenBalanceTypesSchema,
    [WalletType.UNIFIED]: TokenBalanceTypesSchema,
    [WalletType.UNKNOWN]: TokenBalanceTypesSchema,
  } satisfies Record<WalletType, typeof TokenBalanceTypesSchema>)
  .partial();

export const GroupedBalancesByWalletTypeSchema = z.record(
  z.string(),
  TokenBalanceTypesSchema.and(WalletTypeBalancesSchema),
);

export type GroupedBalancesByWalletType = z.infer<typeof GroupedBalancesByWalletTypeSchema>;

export type BalancesByAccountByWalletType = {
  [accountId: string]: BalancesByWalletType;
};

/**
 * @remarks returned type of getBalances sdk method, use numbers for amounts
 */
export type BalancesByWalletType = {
  [walletType: string]: {
    [tokenSymbol: string]: TokenBalanceTypes;
  };
};

/**
 * @remarks returned type of getBalancesV2 sdk method, use strings for amounts
 */
export type BalancesByWalletTypeV2 = {
  [walletType: string]: {
    [tokenSymbol: string]: PortfolioBalanceEntry;
  };
};

export type SubAccountBalancesByWalletType = BalancesByWalletType;
export type SubAccountBalancesByWalletTypeV2 = BalancesByWalletTypeV2;
